import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Screen, ScreenHeader } from "../components/AppShell";
import { Button, StateMessage } from "../components/ui";
import { LanguageToggle } from "../components/LanguageToggle";
import { ArtworkList } from "../features/artworks/ArtworkList";
import { ViewToggle } from "../features/artworks/ViewToggle";
import { SearchBar } from "../features/filters/SearchBar";
import { FilterSheet } from "../features/filters/FilterSheet";
import { ActiveFilters } from "../features/filters/ActiveFilters";
import { useFilters } from "../features/filters/useFilters";
import { hasAnyFilter, activeFilterCount } from "../features/filters/filterState";
import { useArtworks, useFacets, useMe } from "../api/queries";
import { CatalogSwitcher } from "../features/catalog/CatalogSwitcher";
import { CatalogSettingsSheet } from "../features/catalog/CatalogSettingsSheet";
import { useCatalogScope } from "../features/catalog/CatalogProvider";
import { useT } from "../i18n/strings";
import s from "../features/artworks/artworks.module.css";

export default function Home() {
  const t = useT();
  const navigate = useNavigate();
  const { filters, patch, setFilters, clearFilters, clearAll } = useFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const me = useMe();
  const { isOwnCatalog } = useCatalogScope();
  const query = useArtworks(filters);
  const facets = useFacets();

  const rows = query.data?.pages.flatMap((page) => page.results) ?? [];
  const count = activeFilterCount(filters);
  const filtering = hasAnyFilter(filters);

  const onSearch = useCallback((value: string) => patch({ q: value }), [patch]);
  const refresh = useCallback(() => void query.refetch(), [query]);

  return (
    <Screen>
      <ScreenHeader
        sticky
        actions={
          <>
            <ViewToggle />
            <LanguageToggle />
          </>
        }
      >
        {/* The masthead carries the name of the place and how much is in it.
            It scrolls away; search and filters stay. */}
        <div className={s.masthead}>
          <h1 className={s.mastheadTitle}>{t.home.title}</h1>
          {/* /facets counts the whole archive, not the filtered slice, so the
              number would contradict the rows underneath while filtering. The
              chips say what is on; that is the more useful fact anyway. */}
          {facets.data && !filtering && (
            <p className={s.mastheadCount}>
              <span className="mono">{facets.data.total}</span>{" "}
              {t.home.countSuffix(facets.data.total)}
            </p>
          )}
        </div>

        <SearchBar
          value={filters.q}
          onChange={onSearch}
          filterCount={count}
          onOpenFilters={() => setFiltersOpen(true)}
        />
        <CatalogSwitcher
          onOpenSettings={
            me.data?.public_page && isOwnCatalog ? () => setCatalogOpen(true) : undefined
          }
        />
        <ActiveFilters filters={filters} onChange={patch} onClearAll={clearFilters} />
      </ScreenHeader>

      <ArtworkList
        rows={rows}
        isLoading={query.isPending}
        error={query.error}
        hasMore={Boolean(query.hasNextPage)}
        isFetchingMore={query.isFetchingNextPage}
        onLoadMore={() => query.fetchNextPage()}
        onRefresh={refresh}
        empty={
          filtering ? (
            <StateMessage
              icon="search"
              title={t.home.noHitsTitle}
              body={t.home.noHitsBody}
              action={
                <Button variant="secondary" onClick={clearAll}>
                  {t.actions.clearAll}
                </Button>
              }
            />
          ) : (
            <StateMessage
              icon="works"
              title={t.home.emptyTitle}
              body={t.home.emptyBody}
              action={
                <Button variant="secondary" icon="add" onClick={() => navigate("/new")}>
                  {t.tabs.add}
                </Button>
              }
            />
          )
        }
      />

      <FilterSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filters}
        onApply={(next) => setFilters(next)}
      />

      <CatalogSettingsSheet open={catalogOpen} onClose={() => setCatalogOpen(false)} />
    </Screen>
  );
}
